import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ExternalLink, RotateCcw, Sparkles, QrCode, Printer, Smartphone, Lock, FileText, Inbox, Video, Copy, Building2, Share2, Plus, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail, priceLabel, timeAgo } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { BRAND } from "@/lib/brand";
import { VinconexLogo } from "@/components/VinconexMark";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StoryboardPanel } from "@/components/StoryboardPanel";
import { PrintedCodesPanel } from "@/components/PrintedCodesPanel";

const TEST_SHOPPER = { name: "Jordan Miles", phone: "570-555-0134" };

const CARD_QR = [
  { key: "logo", label: "With the Vinconex mark", hint: "Matches your window stickers. Extra error correction, so the logo in the middle never breaks a scan." },
  { key: "plain", label: "Plain black code", hint: "Fewest squares, biggest modules — the safest scan on a small card." },
];

function Open({ href, children, testId }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" data-testid={testId}
       className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#7FC3FF] hover:text-white transition-colors">
      {children} <ExternalLink className="h-3.5 w-3.5" />
    </a>
  );
}

function Step({ n, icon: Icon, title, say, shots, children, testId }) {
  return (
    <div className="relative pl-12 sm:pl-14 pb-10 last:pb-0" data-testid={testId}>
      <span className="absolute left-0 top-0 h-9 w-9 rounded-lg border border-[#0A8CF2]/40 bg-[#0A8CF2]/10 flex items-center justify-center font-mono-tech text-sm font-bold text-[#7FC3FF]">{n}</span>
      <span className="absolute left-[18px] top-10 bottom-0 w-px bg-[#1C2233]" aria-hidden />
      <h3 className="font-heading text-lg md:text-lg font-bold flex items-center gap-2"><Icon className="h-4 w-4 text-[#0A8CF2]" /> {title}</h3>
      {say && (
        <p className="mt-2 rounded-lg border-l-2 border-[#0A8CF2] bg-[#0A8CF2]/5 px-3 py-2 text-sm text-slate-200 italic">“{say}”</p>
      )}
      <ul className="mt-3 space-y-1.5 text-sm text-slate-400">
        {shots.map((s, i) => <li key={i} className="flex gap-2"><span className="text-slate-600">·</span><span>{s}</span></li>)}
      </ul>
      {children && <div className="mt-3">{children}</div>}
    </div>
  );
}

export default function DemoStudio() {
  const [state, setState] = useState(null);
  const [tours, setTours] = useState(null);
  const [invites, setInvites] = useState([]);
  const [invForm, setInvForm] = useState({ dealer_name: "", city: "", state: "", accent_color: "", note: "" });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/public/demo").then(({ data }) => setState(data)).catch(() => toast.error("Couldn't load the demo store")).finally(() => setLoading(false));
  const loadTours = () => api.get("/platform/demo/tours").then(({ data }) => setTours(data)).catch(() => setTours(null));
  const loadInvites = () => api.get("/platform/demo/invites").then(({ data }) => setInvites(data)).catch(() => setInvites([]));
  useEffect(() => { load(); loadTours(); loadInvites(); }, []);

  const createInvite = async () => {
    if (!invForm.dealer_name.trim()) { toast.error("Type the dealership's name first"); return; }
    setBusy("invite");
    try {
      const { data } = await api.post("/platform/demo/invites", invForm);
      setInvites((prev) => [data, ...prev]);
      setInvForm({ dealer_name: "", city: "", state: "", accent_color: "", note: "" });
      navigator.clipboard?.writeText(data.url).catch(() => {});
      toast.success(`Link for ${data.dealer_name} created and copied`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const revokeInvite = async (token) => {
    setBusy(token);
    try {
      await api.delete(`/platform/demo/invites/${token}`);
      loadInvites();
      toast.success("Link turned off");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const run = async (what) => {
    setBusy(what);
    try {
      const { data } = await api.post(`/platform/demo/${what}`);
      setState(data);
      loadTours();
      toast.success(what === "seed" ? `${data.dealer.name} is ready — ${data.vehicles.length} units` : `Demo reset — ${data.cleared_leads || 0} lead(s) cleared`);
    } catch (e) {
      const msg = e.response?.status === 401 || e.response?.status === 403
        ? "Sign in as Vinconex staff first (the admin console), then come back to this page."
        : formatApiErrorDetail(e.response?.data?.detail);
      toast.error(msg);
    } finally {
      setBusy("");
    }
  };

  const hero = state?.hero;
  const copy = (text, label) => {
    if (!navigator.clipboard?.writeText) { toast.info(text); return; }
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`)).catch(() => toast.info(text));
  };

  return (
    <div className="min-h-screen bg-[#05070C] text-white grid-noise">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/70 border-b border-[#1C2233]">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <VinconexLogo variant="wordmark" className="h-6 w-auto" />
          <nav className="flex items-center gap-2">
            <Link to="/" className="text-sm text-slate-300 hover:text-white px-3 py-2" data-testid="demo-home-link">Home</Link>
            <Link to="/dashboard/platform"><Button variant="outline" data-testid="demo-console-btn" className="border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200">Staff console</Button></Link>
          </nav>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#0A8CF2] font-mono-tech mb-3">
          <Video className="h-3.5 w-3.5" /> Demo studio · internal
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight">Record the sales video<br className="hidden sm:block" /> against a perfect store.</h1>
        <p className="mt-4 text-base text-slate-400 max-w-2xl">
          Every screen below belongs to <b className="text-slate-200">{state?.dealer?.name || "Vinconex Demo Motors"}</b> — a fictional two-rooftop store with its own inventory,
          lead walls and CRM inbox. No real dealership, shopper or CARFAX data ever appears on camera. Reset between takes and each recording starts identical.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Button onClick={() => run("seed")} disabled={!!busy} data-testid="demo-seed-btn" className="h-10 bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold">
            {busy === "seed" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="mr-2 h-4 w-4" /> {state?.ready ? "Refresh demo store" : "Create demo store"}</>}
          </Button>
          <Button onClick={() => run("reset")} disabled={!!busy || !state?.ready} variant="outline" data-testid="demo-reset-btn" className="h-10 border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200">
            {busy === "reset" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RotateCcw className="mr-2 h-4 w-4" /> Reset between takes</>}
          </Button>
          <span className="text-xs text-slate-500">Both need a Vinconex staff sign-in.</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#0A8CF2]" /></div>
        ) : !state?.ready ? (
          <div className="mt-10 rounded-xl border border-dashed border-[#2A2E3D] bg-[#0B0D14] p-8 text-center" data-testid="demo-empty">
            <Building2 className="h-8 w-8 mx-auto mb-3 text-slate-600" />
            <p className="text-slate-400">The demo store isn't set up yet. Sign in as Vinconex staff and press <b className="text-slate-200">Create demo store</b> — it builds 10 units across two rooftops in a second.</p>
          </div>
        ) : (
          <>
            <div className="mt-10 grid sm:grid-cols-3 gap-4" data-testid="demo-stats">
              <div className="rounded-xl border border-[#2A2E3D] bg-[#0B0D14] p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-tech">Units on the demo lot</div>
                <div className="font-heading text-2xl font-black mt-1">{state.vehicles.length}</div>
                <div className="text-xs text-slate-500 mt-1">{state.dealer.stores.join(" · ")}</div>
              </div>
              <div className="rounded-xl border border-[#2A2E3D] bg-[#0B0D14] p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-tech">Leads captured this session</div>
                <div className="font-heading text-2xl font-black mt-1" data-testid="demo-lead-count">{state.leads}</div>
                <div className="text-xs text-slate-500 mt-1">{state.scans} QR scans · reset clears both</div>
              </div>
              <div className="rounded-xl border border-[#2A2E3D] bg-[#0B0D14] p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-tech">Walls on camera</div>
                <div className="font-heading text-2xl font-black mt-1">{priceLabel(state.dealer.lower_price_value)} off</div>
                <div className="text-xs text-slate-500 mt-1">CARFAX request · recon · time on lot · trade-in · as-is estimate</div>
              </div>
            </div>

            <section className="mt-10 rounded-xl border border-[#2A2E3D] bg-[#0B0D14] p-5 sm:p-6 flex flex-col sm:flex-row gap-6" data-testid="demo-hero">
              <div className="shrink-0">
                <img src={hero.qr} alt="Demo vehicle QR code" data-testid="demo-qr" className="h-44 w-44 rounded-lg bg-white p-1" />
                <button type="button" onClick={() => copy(hero.scan_url, "Scan link")} data-testid="demo-copy-scan" className="mt-2 text-xs text-slate-400 hover:text-white inline-flex items-center gap-1"><Copy className="h-3 w-3" /> copy scan link</button>
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-tech">Hero car — scan this one on camera</div>
                <h2 className="font-heading text-xl font-extrabold mt-1">{hero.name}</h2>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <Badge variant="outline" className="border-[#0A8CF2]/50 text-[#7FC3FF] text-[10px] font-mono-tech">{priceLabel(hero.price)}</Badge>
                  <Badge variant="outline" className="border-slate-700 text-slate-300 text-[10px] font-mono-tech">Stock {hero.stock_number}</Badge>
                  <Badge variant="outline" className="border-slate-700 text-slate-300 text-[10px] font-mono-tech">{hero.store}</Badge>
                  <Badge variant="outline" className="border-emerald-700/50 text-emerald-400 text-[10px] font-mono-tech">Arrived {hero.lot_age_days} day{hero.lot_age_days === 1 ? "" : "s"} ago</Badge>
                </div>
                <p className="text-sm text-slate-400 mt-3">
                  Hold a real phone up to this code on camera — it opens the same page a shopper gets from the windshield sticker.
                  Shot 3 of the script is far more convincing with a real phone than with a screen recording.
                </p>
                <div className="flex flex-wrap gap-4 mt-3">
                  <Open href={state.links.vehicle} testId="demo-open-vehicle">Open the scan page</Open>
                  <Open href={state.links.sticker} testId="demo-open-sticker">Printable sticker</Open>
                  <Open href={state.links.dealer_page} testId="demo-open-dealer">Demo dealer page</Open>
                </div>
              </div>
            </section>

            <section className="mt-12" data-testid="demo-steps">
              <h2 className="font-heading text-2xl font-black tracking-tight mb-6">Shoot it in this order</h2>

              <Step n="1" icon={RotateCcw} title="Reset, then sign in and enter the demo store" testId="demo-step-1"
                shots={["Press Reset between takes — leads, scans and print flags go back to zero.",
                        "Staff console → Platform → Enter dashboard on Vinconex Demo Motors, so the dashboard shots show demo data only.",
                        "Browser at 1920×1080, 100% zoom, bookmarks hidden, cursor highlight on."]}>
                <Open href={state.links.platform} testId="demo-open-platform">Open the staff console</Open>
              </Step>

              <Step n="2" icon={Printer} title="Inventory → print the stickers" testId="demo-step-2"
                say="Vinconex puts a QR code on every car you own — printed straight from your own inventory."
                shots={["Inventory page: store pills (Chevrolet / Kia) with new·used counts.",
                        "Click Print all unprinted · Demo Motors Kia — the sheet opens with the rooftop name on each sticker.",
                        "Hold on the sheet for a beat, then cut to the printed-sticker stack frame."]}>
                <Open href={state.links.inventory} testId="demo-open-inventory">Open Inventory</Open>
              </Step>

              <Step n="3" icon={QrCode} title="Scan the sticker with a real phone" testId="demo-step-3"
                say="A shopper scans it and gets your branded page in about a second."
                shots={["Point the phone at the QR above (or a printed sticker taped to a car window).",
                        "Keep the phone screen in frame while the page loads — that load speed is the sell."]} />

              <Step n="4" icon={Smartphone} title="The scan page" testId="demo-step-4"
                say="Photos, mileage, the vehicle history, how long it's been on your lot."
                shots={["Scroll slowly: gallery → specs → “Arrived 2 days ago”.",
                        "Pause on the price row before you tap it."]}>
                <Open href={state.links.vehicle} testId="demo-open-vehicle-2">Open the scan page</Open>
              </Step>

              <Step n="5" icon={Lock} title="The lead wall" testId="demo-step-5"
                say="To see your best price, they leave a name and a mobile number. That's the whole trade."
                shots={[`Always type the same test shopper: ${TEST_SHOPPER.name} · ${TEST_SHOPPER.phone} — every take matches.`,
                        "Tap Show me the price; hold on the unlocked price animating in.",
                        "Then the Insider details card: recon completed, 2 days on lot, local trade-in.",
                        "Then Request CARFAX Report — no fake report links anywhere, the shopper asks and you answer."]}>
                <button type="button" onClick={() => copy(`${TEST_SHOPPER.name} ${TEST_SHOPPER.phone}`, "Test shopper")} data-testid="demo-copy-shopper" className="text-sm text-[#7FC3FF] hover:text-white inline-flex items-center gap-1.5"><Copy className="h-3.5 w-3.5" /> copy the test shopper</button>
              </Step>

              <Step n="6" icon={FileText} title="The as-is estimate" testId="demo-step-6"
                say="Anything you'd normally explain in person can sit behind the same ask."
                shots={["Open the 2020 BMW 540i (stock C20330A) — amber AS-IS badge with a $2,240 estimate.",
                        "Tap the badge to show the line items a shopper sees once they've left their info."]} />

              <Step n="7" icon={Inbox} title="The dealer side: lead + CRM" testId="demo-step-7"
                say="Inside a few seconds that lead is in your CRM — with the car, the price they were shown, and the rooftop it belongs to."
                shots={["Leads page: the new lead row with its store badge and unlock badge.",
                        "Open View ADF and hold for 2 seconds — that XML is what the CRM ingests.",
                        `ADF email for the demo store goes to ${state.dealer.crm_email} (your own inbox), never a real dealer CRM.`]}>
                <Open href={state.links.leads_page} testId="demo-open-leads">Open Leads</Open>
              </Step>

              <Step n="8" icon={Building2} title="Close on the multi-rooftop view" testId="demo-step-8"
                say="Your stickers, your branding, your CRM. Vinconex just makes sure the car can answer for itself."
                shots={["Staff console: both rooftops, vehicles, leads, sync health at a glance.",
                        "End card: Vinconex wordmark · Scan. Connect. Drive sales."]}>
                <Open href={state.links.platform} testId="demo-open-platform-2">Open the staff console</Open>
              </Step>
            </section>

            <StoryboardPanel />


            <section className="mt-6 rounded-xl border border-[#2A2E3D] bg-[#0B0D14] p-5" data-testid="demo-invites">
              <h2 className="font-heading font-bold flex items-center gap-2"><Building2 className="h-4 w-4 text-[#0A8CF2]" /> Branded links · one per prospect</h2>
              <p className="text-xs text-slate-400 mt-1">
                The prospect sees the demo cars under <b className="text-slate-200">their own</b> dealership name — scan page, lot page and the lead that lands.
                Every open, unlock and lead is tagged to them, and you get an email the moment they unlock a price.
              </p>
              <div className="grid sm:grid-cols-12 gap-2 mt-4">
                <Input value={invForm.dealer_name} onChange={(e) => setInvForm({ ...invForm, dealer_name: e.target.value })} placeholder="Honesdale Chrysler Dodge Jeep Ram"
                       data-testid="invite-name-input" className="sm:col-span-5 bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100" />
                <Input value={invForm.city} onChange={(e) => setInvForm({ ...invForm, city: e.target.value })} placeholder="City"
                       data-testid="invite-city-input" className="sm:col-span-2 bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100" />
                <Input value={invForm.state} onChange={(e) => setInvForm({ ...invForm, state: e.target.value })} placeholder="PA"
                       data-testid="invite-state-input" className="sm:col-span-1 bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100" />
                <Input value={invForm.accent_color} onChange={(e) => setInvForm({ ...invForm, accent_color: e.target.value })} placeholder="#C8102E"
                       data-testid="invite-accent-input" className="sm:col-span-2 bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100 font-mono-tech" />
                <Button onClick={createInvite} disabled={!!busy} data-testid="invite-create-btn" className="sm:col-span-2 bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold">
                  {busy === "invite" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1.5 h-4 w-4" /> Create link</>}
                </Button>
              </div>

              {invites.length > 0 && (
                <div className="mt-4 space-y-2">
                  {invites.map((i) => (
                    <div key={i.token} data-testid={`invite-${i.token}`} className={`rounded-lg border px-3 py-2.5 ${i.revoked_at ? "border-slate-800 bg-[#05070C] opacity-60" : "border-[#1C2233] bg-[#05070C]"}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-sm">{i.dealer_name}</span>
                        {i.where && <span className="text-xs text-slate-500">{i.where}</span>}
                        {i.revoked_at
                          ? <Badge variant="outline" className="border-slate-700 text-slate-500 text-[10px] font-mono-tech">off</Badge>
                          : <Badge variant="outline" className="border-emerald-700/50 text-emerald-400 text-[10px] font-mono-tech">live</Badge>}
                        <span className="text-[10px] font-mono-tech text-slate-500">{i.opens} open{i.opens === 1 ? "" : "s"} · {i.leads} lead{i.leads === 1 ? "" : "s"}{i.steps.length ? ` · ${i.steps.join(" → ")}` : ""}{i.last_open_at ? ` · ${timeAgo(i.last_open_at)}` : ""}</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 mt-1.5">
                        <code className="text-xs text-[#7FC3FF] break-all">{i.url}</code>
                        <button type="button" onClick={() => copy(i.url, "Link")} data-testid={`invite-copy-${i.token}`} className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1"><Copy className="h-3 w-3" /> copy</button>
                        <a href={`/try/${i.token}`} target="_blank" rel="noreferrer" data-testid={`invite-open-${i.token}`} className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1">open <ExternalLink className="h-3 w-3" /></a>
                        {!i.revoked_at && (
                          <button type="button" onClick={() => revokeInvite(i.token)} disabled={busy === i.token} data-testid={`invite-revoke-${i.token}`} className="text-xs text-red-400/80 hover:text-red-300 inline-flex items-center gap-1 disabled:opacity-50">
                            {busy === i.token ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />} turn off
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="mt-6 rounded-xl border border-[#2A2E3D] bg-[#0B0D14] p-5" data-testid="demo-share">
              <h2 className="font-heading font-bold flex items-center gap-2"><Share2 className="h-4 w-4 text-[#0A8CF2]" /> Send it to a dealer instead</h2>
              <p className="text-xs text-slate-400 mt-1">
                The same demo store powers a shareable click-through tour and a printable leave-behind sheet — no signup, nothing installed.
                Add <code className="text-[#7FC3FF]">?ref=name</code> to the tour link to see who tried it.
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-3">
                <Open href="/try" testId="demo-open-try">Open the dealer tour (/try)</Open>
                <button type="button" onClick={() => copy(`${window.location.origin}/try`, "Tour link")} data-testid="demo-copy-try" className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-1.5"><Copy className="h-3.5 w-3.5" /> copy tour link</button>
                <Open href="/onepager" testId="demo-open-onepager">Printable one-pager (/onepager)</Open>
              </div>
              {tours && (
                <div className="mt-4 border-t border-[#1C2233] pt-3" data-testid="demo-tours">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-tech mb-2">
                    Prospect tours · {tours.total} opened · {tours.with_lead} left a lead
                  </div>
                  {tours.tours.length === 0 ? (
                    <p className="text-xs text-slate-500">Nobody has opened the tour yet.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {tours.tours.slice(0, 8).map((t) => (
                        <div key={t.session_id} data-testid={`demo-tour-${t.session_id}`} className="flex items-center gap-3 text-xs text-slate-300 rounded-md border border-slate-800 bg-[#05070C] px-3 py-2">
                          <span className="truncate flex-1">{t.ref || "direct link"} <span className="text-slate-500">· {t.steps.join(" → ")}</span></span>
                          <span className="text-slate-500 shrink-0">{timeAgo(t.last_at)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </section>

            <section className="mt-6 rounded-xl border border-[#2A2E3D] bg-[#0B0D14] p-5" data-testid="demo-inventory-list">
              <h2 className="font-heading font-bold mb-3">Demo lot · {state.vehicles.length} units</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {state.vehicles.map((v) => (
                  <a key={v.id} href={`/v/${v.id}?qr=1`} target="_blank" rel="noreferrer" data-testid={`demo-vehicle-${v.stock_number}`}
                     className="group rounded-lg border border-[#1C2233] bg-[#05070C] overflow-hidden hover:border-[#0A8CF2]/50 transition-colors">
                    <img src={v.image} alt="" className="h-28 w-full object-cover opacity-90 group-hover:opacity-100" />
                    <div className="p-3">
                      <div className="text-sm font-semibold truncate">{v.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{priceLabel(v.price)} · {v.condition} · {v.stock_number}</div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-600 font-mono-tech mt-1">{v.store}{v.as_is ? " · as-is" : ""}</div>
                    </div>
                  </a>
                ))}
              </div>
            </section>
          </>
        )}

        <section className="mt-6 rounded-xl border border-[#2A2E3D] bg-[#0B0D14] p-5" data-testid="demo-card-qr">
          <h2 className="font-heading font-bold flex items-center gap-2"><QrCode className="h-4 w-4 text-[#0A8CF2]" /> Business-card QR · www.vinconex.com</h2>
          <p className="text-xs text-slate-400 mt-1">
            Print-ready files for a card, sticker or flyer. Keep the code at least <b className="text-slate-200">0.8 in / 20 mm</b> square
            and leave the white edge around it — that quiet zone is what lets a phone lock on. Hand the SVG to a printer when you can.
          </p>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            {CARD_QR.map((c) => (
              <div key={c.key} className="rounded-lg border border-[#1C2233] bg-[#05070C] p-4 flex gap-4 items-center" data-testid={`card-qr-${c.key}`}>
                <img src={`/brand/qr/vinconex-card-qr-${c.key}.png`} alt={`${c.label} QR code`} data-testid={`card-qr-img-${c.key}`}
                     className="h-24 w-24 rounded bg-white p-1 shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold">{c.label}</div>
                  <p className="text-xs text-slate-500 mt-0.5">{c.hint}</p>
                  <div className="flex flex-wrap gap-3 mt-2 text-xs font-semibold">
                    <a href={`/brand/qr/vinconex-card-qr-${c.key}.png`} download data-testid={`card-qr-png-${c.key}`} className="text-[#7FC3FF] hover:text-white">PNG 2000 px</a>
                    <a href={`/brand/qr/vinconex-card-qr-${c.key}.svg`} download data-testid={`card-qr-svg-${c.key}`} className="text-[#7FC3FF] hover:text-white">SVG vector</a>
                    <a href={`/brand/qr/vinconex-card-qr-${c.key}-transparent.png`} download data-testid={`card-qr-dark-${c.key}`} className="text-slate-400 hover:text-white">PNG for dark stock</a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <PrintedCodesPanel onCopy={copy} />

        <p className="mt-10 text-xs text-slate-600" data-testid="demo-owner-line">{BRAND.owner} · internal production page — not linked from the public site.</p>
      </main>
    </div>
  );
}
